import React from 'react';
import { api, resolveUrl, type ChatSummary } from '../../api';
import { useTheme } from '../../theme';
import { Loader } from '../Primitives';
import { NAV_MAIN_H, NAV_TABS_H } from '../TopNav';

// ─────────────────────────────────────────────────────────────────
// CH1: chattlistan (bottenmenyns Chatt) – alla chattar man är med i, som
// i WhatsApp: bild, namn, senaste meddelandet, tid och en röd bubbla med
// antal olästa. Tryck öppnar chatten (markens gruppchatt) med en
// tillbakaknapp hit (CH2).
// ─────────────────────────────────────────────────────────────────

/** Gruppchatten ber App att visa chattlistan (tillbakaknappen i en chatt). */
export const OPEN_CHATS_EVENT = 'qvenari:open-chats';
/** Någon chatt har lästs eller fått nytt; bottenmenyns bubbla hämtar om. */
export const CHATS_CHANGED_EVENT = 'qvenari:chats-changed';

const UNREAD_RED = '#e5484d';

/** Röd bubbla med antal (99+ över 99). */
export function UnreadBadge({ n, style }: { n: number; style?: React.CSSProperties }) {
  if (n <= 0) return null;
  return (
    <span aria-hidden style={{
      minWidth: 18, height: 18, padding: '0 5px', boxSizing: 'border-box', borderRadius: 999, background: UNREAD_RED, color: '#fff',
      fontSize: 11, fontWeight: 700, lineHeight: '18px', textAlign: 'center', fontVariantNumeric: 'tabular-nums', flexShrink: 0, ...style,
    }}>{n > 99 ? '99+' : n}</span>
  );
}

/** Tid i listan: klockslag i dag, "Igår", veckodag senaste veckan, annars datum. */
export function formatChatTime(iso: string | null, now = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86_400_000);
  if (days <= 0) return d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return 'Igår';
  if (days < 7) return d.toLocaleDateString('sv-SE', { weekday: 'long' });
  return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
}

/** "Arne: Hej" – egna meddelanden blir "Du: …". */
export function previewLine(c: Pick<ChatSummary, 'lastMessage' | 'lastSenderName' | 'lastSenderEmail'>, myEmail?: string | null): string {
  if (!c.lastMessage) return 'Inga meddelanden ännu';
  const mine = !!myEmail && c.lastSenderEmail?.toLowerCase() === myEmail.toLowerCase();
  const who = mine ? 'Du' : (c.lastSenderName || '').split(' ')[0];
  return who ? `${who}: ${c.lastMessage}` : c.lastMessage;
}

export const unreadTotal = (chats: Pick<ChatSummary, 'unreadCount'>[]) => chats.reduce((n, c) => n + (c.unreadCount || 0), 0);

/** Olästa totalt för bottenmenyn: hämtas var 30:e sekund och när en chatt läses. */
export function useUnreadChats(enabled: boolean): number {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    if (!enabled) { setN(0); return; }
    let alive = true;
    const load = () => { if (document.visibilityState !== 'hidden') api.myChats().then(c => { if (alive) setN(unreadTotal(c)); }).catch(() => {}); };
    load();
    const id = setInterval(load, 30_000);
    window.addEventListener(CHATS_CHANGED_EVENT, load);
    document.addEventListener('visibilitychange', load);
    return () => { alive = false; clearInterval(id); window.removeEventListener(CHATS_CHANGED_EVENT, load); document.removeEventListener('visibilitychange', load); };
  }, [enabled]);
  return n;
}

function ChatAvatar({ c }: { c: ChatSummary }) {
  const T = useTheme();
  const [broken, setBroken] = React.useState(false);
  const initials = c.title.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const box: React.CSSProperties = { width: 48, height: 48, borderRadius: '50%', flexShrink: 0, overflow: 'hidden' };
  if (c.imageUrl && !broken) return <img src={resolveUrl(c.imageUrl)} alt="" onError={() => setBroken(true)} style={{ ...box, objectFit: 'cover', background: T.surface2 }} />;
  return <span aria-hidden style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.amberDim, color: T.amber, fontWeight: 700, fontSize: 16 }}>{initials || '?'}</span>;
}

export function ChatList({ myEmail, onOpen }: { myEmail?: string | null; onOpen: (c: ChatSummary) => void }) {
  const T = useTheme();
  const [chats, setChats] = React.useState<ChatSummary[] | null>(null);
  const [err, setErr] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    const load = () => api.myChats().then(c => { if (alive) { setChats(c); setErr(false); } }).catch(() => { if (alive) setErr(true); });
    load();
    const id = setInterval(load, 15_000);
    window.addEventListener(CHATS_CHANGED_EVENT, load);
    return () => { alive = false; clearInterval(id); window.removeEventListener(CHATS_CHANGED_EVENT, load); };
  }, []);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '16px 0 24px', paddingTop: `calc(${NAV_MAIN_H + 16}px + var(--qv-tabs-h, ${NAV_TABS_H}px) + env(safe-area-inset-top))` }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 16px 8px', color: T.text }}>Chattar</h1>
      {chats === null && !err && <Loader text="Hämtar chattar…" />}
      {err && chats === null && <p role="alert" style={{ color: T.danger, margin: '12px 16px' }}>Kunde inte hämta chattarna. Försök igen om en stund.</p>}
      {chats?.length === 0 && <p style={{ color: T.textSub, margin: '12px 16px', lineHeight: 1.5 }}>Du är inte med i någon chatt ännu. När du går med i en jaktmark hamnar markens chatt här.</p>}
      {!!chats?.length && (
        <ul role="list" aria-label="Chattar" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {chats.map(c => {
            const unread = c.unreadCount > 0;
            return (
              <li key={c.key}>
                <button type="button" onClick={() => onOpen(c)}
                  aria-label={`${c.title}${unread ? `, ${c.unreadCount} olästa` : ''}`}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', minHeight: 68, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', color: T.text }}>
                  <ChatAvatar c={c} />
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3, borderBottom: `1px solid ${T.border}`, paddingBottom: 10, marginBottom: -10 }}>
                    <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
                      <span style={{ fontSize: 12, color: unread ? UNREAD_RED : T.textMuted, fontWeight: unread ? 700 : 500, flexShrink: 0 }}>{formatChatTime(c.lastAt)}</span>
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 14, color: unread ? T.text : T.textSub, fontWeight: unread ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewLine(c, myEmail)}</span>
                      <UnreadBadge n={c.unreadCount} style={{ height: 20, minWidth: 20, lineHeight: '20px', fontSize: 12 }} />
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
